import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Add, Edit, Delete, Close, Save } from '@mui/icons-material';
import { instructionsAPI } from '../../services/api';

const Instructions = () => {
    const [instructions, setInstructions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        instruction_key: '',
        instruction_text: '',
        category: 'general',
        priority: 1,
        is_active: true
    });

    useEffect(() => {
        console.log('Fetching instructions...');
        fetchInstructions();
    }, []);

    const fetchInstructions = async () => {
        try {
            const response = await instructionsAPI.getAll();
            setInstructions(response.data.data || []);
        } catch (error) {
            console.error('Error fetching instructions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingId) {
                await instructionsAPI.update(editingId, formData);
            } else {
                await instructionsAPI.create(formData);
            }
            fetchInstructions();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving instruction:', error);
            alert('Error saving instruction');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this instruction?')) {
            try {
                await instructionsAPI.delete(id);
                fetchInstructions();
            } catch (error) {
                console.error('Error deleting instruction:', error);
            }
        }
    };

    const handleEdit = (instruction) => {
        setEditingId(instruction.id);
        setFormData({
            instruction_key: instruction.instruction_key,
            instruction_text: instruction.instruction_text,
            category: instruction.category,
            priority: instruction.priority,
            is_active: instruction.is_active
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingId(null);
        setFormData({
            instruction_key: '',
            instruction_text: '',
            category: 'general',
            priority: 1,
            is_active: true
        });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        Chatbot Instructions
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage bot behavior and response guidelines
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setShowModal(true)}
                >
                    Add Instruction
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Instruction</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instructions.map((instruction) => (
                            <TableRow key={instruction.id} hover>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'monospace',
                                            bgcolor: 'grey.100',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            display: 'inline-block',
                                        }}
                                    >
                                        {instruction.instruction_key}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ maxWidth: 300 }}>
                                    <Typography variant="body2" noWrap>
                                        {instruction.instruction_text}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip label={instruction.category} size="small" />
                                </TableCell>
                                <TableCell>{instruction.priority}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={instruction.is_active ? 'Active' : 'Inactive'}
                                        color={instruction.is_active ? 'success' : 'error'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleEdit(instruction)}
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(instruction.id)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={showModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingId ? 'Edit Instruction' : 'Add New Instruction'}
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent dividers>
                        <TextField
                            fullWidth
                            label="Instruction Key"
                            value={formData.instruction_key}
                            onChange={(e) => setFormData({ ...formData, instruction_key: e.target.value })}
                            placeholder="e.g., greeting, product_inquiry"
                            required
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            label="Instruction Text"
                            value={formData.instruction_text}
                            onChange={(e) => setFormData({ ...formData, instruction_text: e.target.value })}
                            placeholder="Enter instruction details..."
                            required
                            multiline
                            rows={4}
                            sx={{ mb: 2 }}
                        />

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    value={formData.category}
                                    label="Category"
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                >
                                    <MenuItem value="general">General</MenuItem>
                                    <MenuItem value="system">System</MenuItem>
                                    <MenuItem value="behavior">Behavior</MenuItem>
                                    <MenuItem value="sales">Sales</MenuItem>
                                    <MenuItem value="support">Support</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                type="number"
                                label="Priority"
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                                inputProps={{ min: 1, max: 100 }}
                            />
                        </Box>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                            }
                            label="Active"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseModal}>Cancel</Button>
                        <Button type="submit" variant="contained" startIcon={<Save />}>
                            {editingId ? 'Update' : 'Create'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Box>
    );
};

export default Instructions;
